import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { clearInteractiveLoginAttempt } from "../auth/acquireIdToken";
import type { User, AppRole } from "@domaddress/shared";
import { canViewActivityLog, hasOrgWideContactReadAccess } from "@domaddress/shared";
import { api } from "../lib/api";
import { DOMINICUS_LOGO_URL } from "../brand";
import { MDR_UNREAD_REFRESH } from "../lib/memberDetailRequestsEvents";
import { GlobalVcardEmailModal } from "./VcardEmailSendParts";

const ROL_KORT: Record<AppRole, string> = {
  deelnemer: "Deelnemer",
  lezer_basis: "Lezer (basis)",
  lezer_volledig: "Lezer (volledig)",
  secretaris: "Secretaris",
  vrijwilliger_coordinator: "Vrijwilliger-coörd.",
  beheerder: "Beheerder",
};

interface Props {
  user: User | null;
  children: React.ReactNode;
}

const MOBILE_NAV_MQ = "(max-width: 768px)";

export default function Layout({ user, children }: Props) {
  const { instance } = useMsal();
  const location = useLocation();
  const [requestUnread, setRequestUnread] = useState(0);
  /** Op smalle schermen: navigatie in een uitschuifmenu i.p.v. één horizontale rij. */
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const onChange = () => {
      if (!mq.matches) closeMobileNav();
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [closeMobileNav]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobileNav();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileNavOpen, closeMobileNav]);

  useEffect(() => {
    if (!user) {
      setRequestUnread(0);
      return;
    }
    const load = async () => {
      try {
        const r = await api.get<{
          data: { count?: number; inbox?: number; outbox?: number; intakes_pending?: number };
        }>("/api/member-detail-requests/unread-count");
        const d = r.data.data;
        if (d?.count != null) {
          setRequestUnread(d.count);
        } else {
          setRequestUnread((d?.inbox ?? 0) + (d?.outbox ?? 0) + (d?.intakes_pending ?? 0));
        }
      } catch {
        setRequestUnread(0);
      }
    };
    void load();
    const t = setInterval(load, 45_000);
    const onMdrRefresh = () => {
      void load();
    };
    window.addEventListener(MDR_UNREAD_REFRESH, onMdrRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener(MDR_UNREAD_REFRESH, onMdrRefresh);
    };
  }, [user]);

  /** Toon het blok “Beheer” als iemand minstens één beheer-link ziet. */
  const showBeheer = !!user && (user.is_super_admin || canViewActivityLog(user));

  const handleLogout = () => {
    void (async () => {
      try {
        await api.post("/api/audit/client-event", { event: "auth.logout" });
      } catch {
        // toch afmelden
      }
      localStorage.removeItem("access_token");
      clearInteractiveLoginAttempt();
      instance.logoutRedirect();
    })();
  };

  return (
    <div className={`layout${mobileNavOpen ? " layout--nav-open" : ""}`}>
      <header className="layout-mobile-header">
        <button
          type="button"
          className="layout-mobile-menu-btn"
          onClick={() => setMobileNavOpen((o) => !o)}
          aria-expanded={mobileNavOpen}
          aria-controls="app-sidebar-nav"
          aria-label={mobileNavOpen ? "Menu sluiten" : "Menu openen"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            {mobileNavOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </>
            )}
          </svg>
        </button>
        <div className="layout-mobile-header-brand">
          <img
            className="layout-mobile-header-logo"
            src={DOMINICUS_LOGO_URL}
            alt=""
            width={40}
            height={40}
            decoding="async"
          />
          <span className="layout-mobile-header-title">Adresboek</span>
        </div>
      </header>

      {mobileNavOpen && (
        <div
          className="sidebar-backdrop"
          role="presentation"
          aria-hidden
          onClick={closeMobileNav}
        />
      )}

      <aside
        className="sidebar"
        id="app-sidebar"
        aria-label="Hoofdnavigatie"
        inert={isNarrowViewport && !mobileNavOpen ? true : undefined}
      >
        <div className="sidebar-logo">
          <img
            className="sidebar-logo-mark"
            src={DOMINICUS_LOGO_URL}
            alt="Dominicus Amsterdam"
            width={132}
            height={40}
            loading="eager"
            decoding="async"
          />
          <div className="sidebar-logo-text">
            <span className="sidebar-title">Adresboek</span>
          </div>
        </div>

        <nav className="sidebar-nav" id="app-sidebar-nav">
          <NavLink
            to="/personeel"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={closeMobileNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
              <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
            </svg>
            Personeel
          </NavLink>
          <NavLink
            to="/vrijwilligers"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={closeMobileNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Vrijwilligers
          </NavLink>
          <NavLink
            to="/dominicusgangers"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={closeMobileNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Dominicusgangers
          </NavLink>
          <NavLink
            to="/externe-contacten"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={closeMobileNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M22 9l3-3-3-3"/><path d="M18.5 6H22"/>
            </svg>
            Externe contacten
          </NavLink>
          <NavLink
            to="/leveranciers"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={closeMobileNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/>
              <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
            Leveranciers
          </NavLink>
          {user && hasOrgWideContactReadAccess(user) && (
            <NavLink
              to="/alle-contacten"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title="Zoeken en filteren in alle adresboekkaarten (alle typen)"
              onClick={closeMobileNav}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                <line x1="15" y1="3" x2="15" y2="21" strokeDasharray="2 2" />
              </svg>
              Alle contacten
            </NavLink>
          )}
          <NavLink
            to="/groups"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={closeMobileNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
            Groepen
          </NavLink>
          <NavLink
            to="/aanvragen"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={closeMobileNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <path d="M22 6l-10 7L2 6" />
            </svg>
            Aanvragen
            {requestUnread > 0 && (
              <span
                className="nav-badge"
                style={{
                  marginLeft: 6,
                  fontSize: "0.7rem",
                  background: "var(--accent, #ee2361)",
                  color: "#fff",
                  borderRadius: 999,
                  padding: "0 0.4rem",
                  minWidth: "1.1rem",
                  textAlign: "center",
                }}
                title="Aanvragen: ongelezen binnenkomend, nieuw antwoord op je verzoek, en/of open werkgroep-intakes — op de pagina zie je per tab hoeveel"
              >
                {requestUnread > 99 ? "99+" : requestUnread}
              </span>
            )}
          </NavLink>
          {showBeheer && <div className="sidebar-nav-label">Beheer</div>}
          {user && canViewActivityLog(user) && (
            <NavLink
              to="/admin/users"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title="Gebruikers, app-rollen (Deelnemer, Secretaris, …) en koppeling aan vrijwilliger/personeelsprofiel"
              onClick={closeMobileNav}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              {"Beheer"}
            </NavLink>
          )}
          {user && canViewActivityLog(user) && (
            <NavLink
              to="/admin/audit"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title="Wie wat wanneer: wijzigingen, import, aan- en afmelden, …"
              onClick={closeMobileNav}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 8v4l2.5 1.5" />
                <circle cx="12" cy="12" r="9" />
              </svg>
              Activiteiten
            </NavLink>
          )}
          {user && canViewActivityLog(user) && (
            <NavLink
              to="/admin/tools"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title="Eenmalig onderhoud, o.a. telefoonnummers opschonen"
              onClick={closeMobileNav}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
              Scripts
            </NavLink>
          )}
          {user && canViewActivityLog(user) && (
            <NavLink
              to="/admin/mailerlite-import"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title="MailerLite-abonnees ophalen en koppelen aan DomAddress-kaarten"
              onClick={closeMobileNav}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="8 17 12 21 16 17"/>
                <line x1="12" y1="3" x2="12" y2="21"/>
              </svg>
              MailerLite import
            </NavLink>
          )}
          {user && canViewActivityLog(user) && (
            <NavLink
              to="/admin/mailerlite-inbox"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title="Wijzigingen uit MailerLite goedkeuren voordat ze op de kaart komen"
              onClick={closeMobileNav}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              MailerLite-inbox
            </NavLink>
          )}
        </nav>

        <div className="sidebar-user">
          {user && (
            <>
              <div className="user-avatar">
                {user.avatar_url
                  ? <img src={user.avatar_url} alt={user.name} />
                  : <span>{user.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                }
              </div>
              <div className="user-info">
                <span className="user-name">{user.name}</span>
                <span className="user-email">{user.email}</span>
                {user.is_super_admin ? (
                  <span className="data-list-row-muted" style={{ fontSize: "0.75rem", display: "block" }}>
                    Superbeheerder
                  </span>
                ) : user.app_role ? (
                  <span className="data-list-row-muted" style={{ fontSize: "0.75rem", display: "block" }}>
                    Rol: {ROL_KORT[user.app_role] ?? user.app_role}
                  </span>
                ) : null}
              </div>
            </>
          )}
          <button
            className="btn-logout"
            onClick={() => {
              closeMobileNav();
              handleLogout();
            }}
            title="Afmelden"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </aside>
      <main className="main-content">
        {children}
      </main>
      <GlobalVcardEmailModal />
    </div>
  );
}
